
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HouseService, TokenStorageService } from '../../core/services';
import { finalize } from 'rxjs/operators';
import { NewEditHouseComponent } from './new-edit-house/new-edit-house.component';
import { EntityListComponent, EntityListMenuAction, TableHeaderAndValueKeySets } from '../../core/ui/entity-list/entity-list.component';
import { ConfirmActionModalComponent } from '../../core/ui/confirm-action-modal/confirm-action-modal.component';
import { TranslatePipe } from '../../core/pipes/translate.pipe';
import {
  appendUniqueById,
  createListPaginationState,
  hasMoreByTotal,
  resolveRequestPage,
  resetListPaginationState,
  updateAfterFetch
} from '../../core/utils/list-pagination.util';

interface House {
  id: number;
  accountId?: number | null;
  houseName: string;
  houseNo: string;
  houseAddress: string;
  totalFloor?: number | null;
  totalFlat?: number | null;
  status: number;
  totalIncome?: number;
  paymentStatus?: string;
  floors?: string[];
  createdOn: string;
  updatedOn: string | null;
  actionType: any;
  appUser: any;
}

interface ApiResponse {
  code: string;
  message: string;
  refId: string | null;
  totalRows: number;
  data: House[];
  totals: any;
}

interface HouseListRow extends House {
  totalIncomeLabel: string;
  statusLabelText: string;
}

@Component({
  selector: 'app-house-list',
  standalone: true,
  imports: [CommonModule, NewEditHouseComponent, EntityListComponent, ConfirmActionModalComponent, TranslatePipe],
  templateUrl: './house-list.component.html',
  styleUrls: ['./house-list.component.css']
})
export class HouseListComponent implements OnInit {
  isAuthenticated = false;
  isLoading = false;
  isLoadingMore = false;
  houseList: HouseListRow[] = [];
  error: string | null = null;
  status: string | null = null;
  filters: Record<string, any> = {};
  private readonly paginationState = createListPaginationState();
  selectedHouse: House | null = null;
  pendingDeleteHouse: House | null = null;
  isDetailsOpen = false;
  isEditOpen = false;
  isDeleting = false;
  tableHeaderAndValueKeySets: TableHeaderAndValueKeySets = {
    colorKey: 'status',
    'table.name': {
      topLineKey: 'houseName',
      bottomLineKyes: ['houseAddress']
    },
    'table.totalIncome': 'totalIncomeLabel'
  };
  readonly houseMenuActions: EntityListMenuAction[] = [
    { key: 'edit', label: 'action.edit' },
    { key: 'delete', label: 'action.delete', danger: true, requiredService: 'HOUSE_DELETE' }
  ];

  get deletedMode(): boolean {
    return Number(this.filters?.['status']) === 0;
  }

  constructor(
    private readonly houses: HouseService,
    private readonly tokens: TokenStorageService
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.tokens.hasActiveSession;
    if (this.isAuthenticated) {
      this.status = 'Loading house list...';
      this.fetchHouses(true);
    } else {
      this.error = 'Please log in first.';
    }
  }

  get hasMoreHouses(): boolean {
    return hasMoreByTotal(this.houseList, this.paginationState);
  }

  fetchHouses(reset = true): void {
    if ((reset && this.isLoading) || (!reset && (this.isLoading || this.isLoadingMore || !this.hasMoreHouses))) {
      return;
    }

    if (reset) {
      this.isLoading = true;
      this.error = null;
      this.houseList = [];
      resetListPaginationState(this.paginationState);
    } else {
      this.isLoadingMore = true;
    }

    const filters = { ...this.filters };
    if (!Object.prototype.hasOwnProperty.call(filters, 'status')) {
      filters['status'] = 1;
    }
    const requestPage = resolveRequestPage(this.paginationState, reset);
    this.houses.list({
      pagination: this.paginationState.pagination,
      pageNo: requestPage,
      rowCount: this.paginationState.rowCount,
      filters
    })
      .pipe(finalize(() => {
        if (reset) {
          this.isLoading = false;
        } else {
          this.isLoadingMore = false;
        }
      }))
      .subscribe({
        next: (res: ApiResponse) => {
          if (res && res.code === '000') {
            const nextRows = (res.data || []).map((house) => ({
              ...house,
              totalIncomeLabel: this.formatIncome(house),
              statusLabelText: this.statusLabel(house)
            }));
            updateAfterFetch(this.paginationState, res?.totalRows, requestPage, nextRows.length);
            this.houseList = reset ? nextRows : appendUniqueById(this.houseList, nextRows);
            this.status = `Total houses: ${this.paginationState.totalRows || this.houseList.length}`;
          } else {
            this.error = res?.message || 'Unknown error occurred.';
          }
        },
        error: (err: any) => {
          this.error = err.message || 'Error fetching houses';
        },
      });
  }

  setFilters(filters: Record<string, any>): void {
    this.filters = { ...filters };
    this.fetchHouses(true);
  }

  onLoadMoreFromList(): void {
    this.fetchHouses(false);
  }

  onCardClickFromList(item: Record<string, any>): void {
    this.onHouseCardClick(item as House);
  }

  onEditClickFromList(item: Record<string, any>): void {
    this.openEditHouse(item as House);
  }

  onDeleteClickFromList(item: Record<string, any>): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeleteHouse = item as House;
  }

  formatIncome(house: House): string {
    const rawValue =
      house.totalIncome ??
      (house as any).totalIncomeAmount ??
      (house as any).totalIncomeTk ??
      null;

    if (rawValue === null || rawValue === undefined || Number.isNaN(Number(rawValue))) {
      return 'N/A';
    }

    const value = Number(rawValue);
    return `৳${value.toLocaleString('en-US')}`;
  }

  statusLabel(house: House): string {
    if (house.paymentStatus) {
      return house.paymentStatus;
    }

    return house.status === 1 ? 'Active' : 'Inactive';
  }

  onHouseCardClick(house: House): void {
    this.selectedHouse = house;
    this.isDetailsOpen = true;
  }

  floorNames(house: House | null): string {
    if (!house || !Array.isArray(house.floors) || house.floors.length === 0) {
      return 'N/A';
    }

    return house.floors.join(', ');
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();

    const rawHours = date.getHours();
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const period = rawHours >= 12 ? 'pm' : 'am';
    const hours12 = rawHours % 12 || 12;
    const hours = `${hours12}`.padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes} ${period}`;
  }

  closeDetailsModal(): void {
    this.isDetailsOpen = false;
  }

  openEditHouse(house: House): void {
    this.selectedHouse = house;
    this.isEditOpen = true;
  }

  closeEditHouse(): void {
    this.isEditOpen = false;
  }

  handleEditSave(): void {
    this.isEditOpen = false;
    this.fetchHouses(true);
  }

  closeDeleteModal(): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeleteHouse = null;
  }

  confirmDeleteHouse(): void {
    const house = this.pendingDeleteHouse;
    if (!house || this.isDeleting) {
      return;
    }

    this.isDeleting = true;
    this.error = null;

    this.houses.delete({ id: house.id })
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: () => {
          this.pendingDeleteHouse = null;
          this.fetchHouses(true);
        },
        error: (err: any) => {
          this.pendingDeleteHouse = null;
          this.error = err?.message || 'Failed to delete house.';
        }
      });
  }

}
